import ServiceError from '../../models/classes/ServiceError';
import { TokenizationServiceConfig } from '../../types/ServiceConfigurations';
import {
  ITokenizationService,
  TokenizationContract,
  TokenizationCVVUpdateRequest,
  TokenizationRequest,
} from '../../types/Tokenization';

class TokenizationService implements ITokenizationService {
  private pciUrl = '';

  private enableStoredPaymentProfiles = false;

  private pciMerchantId = '';

  public isInitialized = false;

  constructor(config?: TokenizationServiceConfig) {
    if (config) {
      this.pciUrl = config.apiUrl;
      this.enableStoredPaymentProfiles = config.enableStoredPaymentProfiles;
      this.pciMerchantId = config.pciMerchantId;
      this.isInitialized = true;
    }
  }

  public initialize(config: TokenizationServiceConfig): void {
    this.pciUrl = config.apiUrl;
    this.enableStoredPaymentProfiles = config.enableStoredPaymentProfiles;
    this.pciMerchantId = config.pciMerchantId;
    this.isInitialized = true;
  }

  public async getTokenAsync(
    request: TokenizationRequest
  ): Promise<string | null> {
    const {
      cardNumber,
      cardHolderName,
      expireMonth,
      expireYear,
      cvv,
      cardTypeName,
    } = request;
    const pciContract: TokenizationContract = {
      NumberPart: TokenizationService.numberpart(cardNumber ?? ''),
      PersistCard: this.enableStoredPaymentProfiles,
      CardHolderName: cardHolderName,
      ExpireMonth: expireMonth,
      ExpireYear: expireYear,
      CVV: cvv,
      CardType: cardTypeName,
      Token: '',
    };
    const body = JSON.stringify(pciContract);
    try {
      const pciResponse = await fetch(`${this.pciUrl}/cards`, {
        method: 'POST',
        headers: this.getHeaders(),
        body,
      });
      return TokenizationService.extractToken(pciResponse);
    } catch (error) {
      // TODO: Error trace/logging
      // eslint-disable-next-line no-console
      console.error(error);
      return null;
    }
  }

  public async updateCVVAsync(
    request: TokenizationCVVUpdateRequest
  ): Promise<void> {
    const { cvv, token } = request;
    const pciContract: TokenizationContract = {
      CVV: cvv,
    };
    const body = JSON.stringify(pciContract);

    const response = await fetch(`${this.pciUrl}/cards/${token}`, {
      method: 'PUT',
      headers: this.getHeaders(),
      body,
    });

    if (response.status !== 200) {
      throw new ServiceError(
        'TokenizationService',
        'Unable to tokenize saved card security code'
      );
    }
  }

  private static async extractToken(pciResponse: Response): Promise<string> {
    const tokenString = await pciResponse.text();
    return this.formatToken(tokenString);
  }

  private getHeaders(): RequestInit['headers'] {
    return {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      MerchantId: this.pciMerchantId,
    };
  }

  public static numberpart(FullCardNumber: string): string {
    // return without last 4 digits
    const withoutLast4 = FullCardNumber.substring(0, FullCardNumber.length - 4);
    const NumberPart = `${withoutLast4}****`;
    return NumberPart;
  }

  public static last4(FullCardNumber: string): string {
    // return only last 4 digits
    const Last4 = FullCardNumber.slice(FullCardNumber.length - 4);
    return Last4;
  }

  private static formatToken(rawTokenResponse: string): string {
    return rawTokenResponse.replace(/"/g, '');
  }
}

export default TokenizationService;
